import { isPast, isToday } from 'date-fns';

import type { SupportTier } from '@avalara/types/obligations';
import type { Obligation } from '@avalara/types/ztb';
import { getCookie } from './cookies';

const getAppEnvironment = () => (typeof window !== 'undefined' ? window.AvaConfig?.appEnv : process.env.APP_ENV);

export const itemNameMatchesRegex = (obligation: Obligation, regex: RegExp) => regex.test(obligation.itemName);

export const productNameMatchesRegex = (obligation: Obligation, regex: RegExp) =>
  obligation.productName && regex.test(obligation.productName);

export const isTrialObligation = (obligation: Obligation): boolean => itemNameMatchesRegex(obligation, /Trial/i);

export const anyItemNameMatchesRegex = (obligations: Array<Obligation>, regex: RegExp): boolean =>
  obligations.some((obligation) => itemNameMatchesRegex(obligation, regex));

export const isSandboxTrialObligation = (obligations: Array<Obligation>) =>
  getAppEnvironment() === 'sbx' && anyItemNameMatchesRegex(obligations, /Free Sandbox/i);

export const isAvalaraIncludedCustomerIps = (obligations: Array<Obligation>): boolean =>
  obligations.some(({ isAvalaraIncluded }) => isAvalaraIncluded === true);

export const hasPaidAvaTax = (obligations: Array<Obligation>) =>
  obligations.some(
    (obligation) =>
      !isTrialObligation(obligation) &&
      productNameMatchesRegex(obligation, /AvaTax/i) &&
      !itemNameMatchesRegex(obligation, /Pooled Usage Source/i) &&
      !itemNameMatchesRegex(obligation, /Avalara Included/i) && // BigCommerce is unfortunately different than every other AI obligation we've seen
      !isSandboxTrialObligation(obligations),
  );

export const anyFeatureNameMatchesRegex = (obligations: Array<Obligation>, regex: RegExp): boolean =>
  obligations.some((obligation) => itemNameMatchesRegex(obligation, regex));

export const isAvalaraIncludedCustomer = (obligations: Array<Obligation>): boolean =>
  obligations.some(({ isAvalaraIncluded }) => isAvalaraIncluded === true);

export const isSandboxTrial = (obligations: Array<Obligation>) =>
  getAppEnvironment() === 'sbx' && anyFeatureNameMatchesRegex(obligations, /Free Sandbox/i);

export const isBasicReturnsObligation = (obligation: Obligation): boolean =>
  itemNameMatchesRegex(obligation, /Returns for Small Business/i) || itemNameMatchesRegex(obligation, /Basic Returns/i);

export const isRsbTrialObligation = (obligation: Obligation): boolean =>
  isBasicReturnsObligation(obligation) && isTrialObligation(obligation);

export const isPaidRsbObligation = (obligation: Obligation): boolean =>
  isBasicReturnsObligation(obligation) && !isTrialObligation(obligation);

export const hasBasicReturns = (obligations: Array<Obligation>): boolean => obligations.some(isPaidRsbObligation);

export const hasRsbTrial = (obligations: Array<Obligation>): boolean =>
  obligations.some(isRsbTrialObligation) && !obligations.some(isPaidRsbObligation);

export const hasManagedReturns = (obligations: Array<Obligation>) =>
  obligations.some(
    (obligation) =>
      (productNameMatchesRegex(obligation, /Managed Returns/i) ||
        productNameMatchesRegex(obligation, /Avalara Returns/i)) &&
      !isBasicReturnsObligation(obligation),
  );

// Return true if the customer has a free AvaTax trial with no active, paid AvaTax subscription
export const isAvataxFreeTrialCustomer = (obligations: Array<Obligation>) =>
  !hasPaidAvaTax(obligations) &&
  !isAvalaraIncludedCustomer(obligations) &&
  (obligations.some((obligation) => isTrialObligation(obligation) && productNameMatchesRegex(obligation, /Avatax/i)) ||
    isSandboxTrial(obligations));

export const isFreeTrialExtensionPending = (accountId: number | string | null) =>
  Boolean(accountId != null && getCookie(`at_trial_ext_${accountId}`));

// Determine if a user is neither a paid Avatax user nor a free trial customer nor an AI customer
export const isAvatax = (obligations: Array<Obligation>) =>
  hasPaidAvaTax(obligations) ||
  isAvalaraIncludedCustomer(obligations) ||
  isAvataxFreeTrialCustomer(obligations) ||
  isSandboxTrial(obligations);

const isObligationPaidAsbSuite = (obligation: Obligation) =>
  itemNameMatchesRegex(obligation, /Avalara for Small Business/);

export const hasPaidAsbSuite = (obligations: Array<Obligation>): boolean => obligations.some(isObligationPaidAsbSuite);

export const hasPaidAsbSuiteStandard = (obligations: Array<Obligation>): boolean =>
  obligations.some(
    (obligation) => isObligationPaidAsbSuite(obligation) && itemNameMatchesRegex(obligation, /Standard/i),
  );

export const hasPaidAsbSuiteAdvanced = (obligations: Array<Obligation>): boolean =>
  obligations.some(
    (obligation) => isObligationPaidAsbSuite(obligation) && itemNameMatchesRegex(obligation, /Advanced/i),
  );

const isAsbSuiteFreeTrialObligation = (obligation: Obligation): boolean =>
  itemNameMatchesRegex(obligation, /ESB Suite Free Trial/i) || itemNameMatchesRegex(obligation, /ESB Free Trial/i);

export const hasAsbSuiteFreeTrial = (obligations: Array<Obligation>): boolean =>
  obligations.some(isAsbSuiteFreeTrialObligation);

// TODO(Ben): Remove the hard-coded AMZN id after the resolution of https://jira.avalara.com/browse/OPUS-898
export const isExtractor = ({ connectorId, connectorIsExtractor }: Obligation) =>
  connectorId === 'a0n0b00000ODPh2AAH' || (connectorIsExtractor != null && /yes/i.test(connectorIsExtractor));

export const getConnectorIds = (obligations: Array<Obligation>): Array<string> => [
  ...new Set(
    obligations
      .filter((o) => !isExtractor(o))
      .map((o) => o.connectorId || '')
      .filter(Boolean),
  ),
];

export const hasConnectorObligation = (obligations: Array<Obligation>, connectorId: string): boolean =>
  obligations.some((obligation) => obligation.connectorId === connectorId);

export const hasBigCommerceObligation = (obligations: Array<Obligation>): boolean =>
  anyFeatureNameMatchesRegex(obligations, /BigCommerce/i);

const findNewestObligation = (obligations: Array<Obligation>): Obligation | undefined =>
  [...obligations].sort((a, b) => new Date(b.termEnd).getTime() - new Date(a.termEnd).getTime())[0];

export const isSupportObligation = (obligation: Obligation) =>
  (itemNameMatchesRegex(obligation, /Additional Fees/i) || itemNameMatchesRegex(obligation, /Support - /i)) &&
  itemNameMatchesRegex(obligation, /(Enterprise|Business|Professional|Managed) Support/i);

export const getCurrentSupportPlan = (obligations: Array<Obligation>): SupportTier => {
  const supportObligations = obligations.filter(isSupportObligation);
  const newestSupportPlan = findNewestObligation(supportObligations);
  if (!newestSupportPlan) return 'Standard Support';
  if (itemNameMatchesRegex(newestSupportPlan, /Enterprise Support/i)) return 'Enterprise Support';
  if (itemNameMatchesRegex(newestSupportPlan, /Business Support/i)) return 'Business Support';
  if (itemNameMatchesRegex(newestSupportPlan, /Professional Support/i)) return 'Standard Support Plus';
  if (itemNameMatchesRegex(newestSupportPlan, /Managed Support/i)) return 'Managed Support';
  // NOTE (Andrew) - unreachable code:
  return 'Standard Support';
};

export const hasExemptionsObligation = (obligations: Array<Obligation>): boolean =>
  anyFeatureNameMatchesRegex(obligations, /Exemptions/i);

export const isActiveObligation = (obligation: Obligation): boolean =>
  !isPast(new Date(obligation.termEnd)) || isToday(new Date(obligation.termEnd));

export const getHighestActiveAvaTaxTier = (obligations: Array<Obligation>): number =>
  Math.max(
    0,
    ...obligations
      .filter(isActiveObligation)
      .filter((obligation) => itemNameMatchesRegex(obligation, /AvaTax.*Service/i))
      .map(({ quantity }) => quantity || 0),
  );
